import React from "react";
import { hierarchy, type HierarchyRectangularNode } from "d3-hierarchy";
import type { ProfileData } from "@recursive-robot/react-jsx-parser";
import { toTree, nodeLabel, type ProfilerTreeNode } from "../../../store/profiling/selectors";
import { heatColor, textOnHeat, formatMs, TRIGGER_LABELS } from "./viz";
import { ProfilingTooltip, TipRow, TipTitle, TipDivider } from "./ProfilingTooltip";
import { useContainerWidth } from "./useContainerWidth";
import { usePanZoom } from "./usePanZoom";
import CodeBlock from "../CodeBlock";

const ROW_HEIGHT = 22;
const MIN_LABEL_WIDTH = 36;
// Floor for every frame's width. Node timings come from a browser-clamped performance.now()
// clock, so any node faster than the clock resolution measures selfTime 0; without a floor it
// would collapse to zero width and vanish (≥ the 2px rect inset so at least 1px stays visible).
const MIN_FRAME_PX = 3;
// Layout-weight floor for the same clock-granularity reason: in a batch where EVERY node measured
// 0 ms, proportional layout has no weights at all and the whole graph collapses. The epsilon makes
// an all-zero batch degrade to widths proportional to node count, while in a mixed batch it is
// negligible next to any real timing (MIN_FRAME_PX still guarantees visibility there).  Applied to
// the layout only — tooltips/detail keep the true measured values.
const MIN_NODE_SELF_MS = 0.00001;

type Props = {
    batch: ProfileData;
    selectedNodeId: number | null;
    onSelectNode: (nodeId: number) => void;
    /** Node-source filter values: matching frames get an accent stroke, the rest are dimmed. */
    highlightSources?: Set<string> | null;
};

type LaidOut = HierarchyRectangularNode<ProfilerTreeNode>;

type HoverState = { node: LaidOut; x: number; y: number } | null;

function label(n: ProfilerTreeNode): string {
    const base = nodeLabel(n);
    return n.loopIndex != null ? `${base} [${n.loopIndex}]` : base;
}

/**
 * Assign x0/x1 to every node. Width stays proportional to summed self-time (`value`, à la
 * d3.partition), but each frame is floored to MIN_FRAME_PX so zero/tiny-time nodes render
 * side by side instead of collapsing. Children keep their chronological order (from
 * hierarchy.sort); the parent's right-hand self-time gap is preserved except where the
 * minimums have to be carved from it.
 */
function layoutX(node: LaidOut, x0: number, x1: number): void {
    node.x0 = x0;
    node.x1 = x1;
    const kids = node.children as LaidOut[] | undefined;
    if (!kids || kids.length === 0) return;

    const W = x1 - x0;
    const n = kids.length;
    const value = node.value ?? 0;
    const k = value > 0 ? W / value : 0;
    // Never demand more than an equal share, so a wide sibling group degrades to an even split
    // instead of overflowing when n * MIN_FRAME_PX exceeds the available width.
    const min = Math.min(MIN_FRAME_PX, W / n);
    // Proportional share preserves the self-time gap: sum(raw) = W * (Σchild.value / node.value).
    const raw = kids.map(c => (c.value ?? 0) * k);
    let widths = raw.map(r => Math.max(min, r));

    const total = widths.reduce((a, b) => a + b, 0);
    if (total > W) {
        // Flooring overflowed the parent: shrink the above-min frames proportionally to fit,
        // clamping each at the minimum. (Everything already at min can only happen when the
        // even-split guard above holds, in which case total === W and we never get here.)
        const excess = total - W;
        const slack = widths.reduce((a, w) => a + (w > min ? w - min : 0), 0);
        widths = slack > 0
            ? widths.map(w => (w > min ? Math.max(min, w - (excess * (w - min)) / slack) : min))
            : widths.map(() => W / n);
    }

    let cx = x0;
    kids.forEach((c, i) => {
        layoutX(c, cx, cx + widths[i]);
        cx += widths[i];
    });
}

function FlameGraph({ batch, selectedNodeId, onSelectNode, highlightSources }: Props) {
    const [focusId, setFocusId] = React.useState<number | null>(null);
    const [hover, setHover] = React.useState<HoverState>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const viewportRef = React.useRef<HTMLDivElement>(null);
    // Fills its pane and reflows on resize (pop-out-safe — see the hook).
    const width = useContainerWidth(containerRef, 600, 240);
    // Continuous wheel-zoom / drag-pan on top of the click-to-focus mechanism (shared with the timeline).
    const { k, tx, reset, consumeDragClick, viewportHandlers } = usePanZoom(width, viewportRef);

    // Reset focus whenever the batch changes (a new render pass invalidates node ids).
    React.useEffect(() => { setFocusId(null); }, [batch.cycleId, batch.renderId, batch.trigger]);
    // Changing focus already re-scales the view — start it un-zoomed (also covers batch changes).
    React.useEffect(() => { reset(); }, [focusId, batch, reset]);

    const { root, maxHeatSelf } = React.useMemo(() => {
        // Drop whitespace-only parser artifacts (e.g. empty JSXText nodes): they carry no
        // meaningful timing and exist only because of whitespace in the template. Keep an
        // empty-source node if something is parented to it, so we never orphan a real subtree
        // (toTree silently drops nodes whose parent is missing).
        const parentIds = new Set(batch.nodes.map(n => n.parentId));
        const meaningful = batch.nodes.filter(
            n => (n.source ?? "").trim() !== "" || parentIds.has(n.id)
        );
        const roots = toTree(meaningful);
        // Wrap in a synthetic root so a batch with several top-level nodes lays out as one graph.
        const synthetic: ProfilerTreeNode = {
            id: -1, parentId: null, depth: -1, nodeType: batch.trigger, source: "",
            location: { line: 0 }, startTime: batch.startTime, selfTime: 0,
            totalTime: batch.totalTime, loopIndex: undefined, children: roots,
        };
        const h = hierarchy<ProfilerTreeNode>(synthetic, d => d.children)
            // Width is proportional to inclusive time: summing exclusive self-time up the tree
            // reconstructs each subtree's total.
            .sum(d => Math.max(MIN_NODE_SELF_MS, d.selfTime))
            // Order siblings chronologically (left = earliest) so the graph reflects actual render
            // order rather than being re-sorted by duration.
            .sort((a, b) => (a.data.startTime ?? 0) - (b.data.startTime ?? 0)) as LaidOut;
        // Lay out x-coordinates ourselves (rather than d3.partition) so every node is floored to
        // a minimum width and nothing vanishes under the coarse clock; y comes from depth below.
        layoutX(h, 0, width);
        const maxSelf = meaningful.reduce((m, n) => Math.max(m, n.selfTime), 0);
        return { root: h, maxHeatSelf: maxSelf };
    }, [batch, width]);

    const focus = React.useMemo(() => {
        if (focusId == null) return root;
        return root.descendants().find(d => d.data.id === focusId) ?? root;
    }, [root, focusId]);

    const focusSpan = Math.max(1e-6, focus.x1 - focus.x0);
    const ancestorIds = React.useMemo(() => new Set(focus.ancestors().map(a => a.data.id)), [focus]);

    const rows = React.useMemo(() => root.descendants().filter(d => d.data.id !== -1), [root]);
    const totalHeight = (root.height + 1) * ROW_HEIGHT;

    const rectFor = (d: LaidOut): { x: number; w: number } | null => {
        const isAncestor = ancestorIds.has(d.data.id) && d.data.id !== focus.data.id;
        if (isAncestor) return { x: 0, w: width }; // full-width "zoom out" bar — navigation, never pans
        // Only the focused node and its subtree are visible; everything else is clipped away.
        const inFocus = d.x0 >= focus.x0 - 1e-6 && d.x1 <= focus.x1 + 1e-6;
        if (!inFocus) return null;
        // Focus-relative position, then the wheel/drag view transform on top.
        const x = ((d.x0 - focus.x0) / focusSpan) * width * k + tx;
        const w = ((d.x1 - d.x0) / focusSpan) * width * k;
        if (x > width || x + w < 0) return null; // culled out of the panned viewport
        return { x, w };
    };

    return (
        <div className="flamegraph" ref={containerRef}>
            <div className="flamegraph-breadcrumb">
                {focus.ancestors().reverse().map(a => (
                    <React.Fragment key={a.data.id}>
                        <button
                            className="crumb"
                            title={a.data.id === -1 ? "Show the whole batch" : "Zoom back out to this frame"}
                            onClick={() => setFocusId(a.data.id === -1 ? null : a.data.id)}
                        >
                            {a.data.id === -1 ? "root" : label(a.data)}
                        </button>
                        <span className="crumb-sep">›</span>
                    </React.Fragment>
                ))}
                <span className="flamegraph-hint">click a frame to focus · scroll to zoom · drag to pan · width = total time · color = self time</span>
            </div>

            <div className="flamegraph-viewport" ref={viewportRef} {...viewportHandlers}>
                <svg width={width} height={totalHeight} className="flamegraph-svg" role="img">
                    {rows.map(d => {
                        const box = rectFor(d);
                        if (!box || box.w <= 0) return null;
                        const t = maxHeatSelf > 0 ? d.data.selfTime / maxHeatSelf : 0;
                        const fill = heatColor(t);
                        const y = d.data.depth * ROW_HEIGHT;
                        const selected = d.data.id === selectedNodeId;
                        const highlighted = !!highlightSources?.has(d.data.source);
                        const dimmed = !!highlightSources && !highlighted && !selected;
                        return (
                            <g
                                key={d.data.id}
                                transform={`translate(${box.x}, ${y})`}
                                className="flame-node"
                                opacity={dimmed ? 0.45 : 1}
                                onClick={e => {
                                    e.stopPropagation();
                                    if (consumeDragClick()) return; // a pan ended here — not a select
                                    onSelectNode(d.data.id);
                                    setFocusId(d.data.id);
                                }}
                                onMouseMove={e => setHover({ node: d, x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => setHover(h => (h?.node === d ? null : h))}
                            >
                                {/* 2px surface gaps between frames (dataviz spacer) via inset width/height. */}
                                <rect
                                    x={0} y={0}
                                    width={Math.max(0, box.w - 2)}
                                    height={ROW_HEIGHT - 2}
                                    rx={2}
                                    fill={fill}
                                    stroke={highlighted ? "var(--colors-orange-3)" : selected ? "deeppink" : "none"}
                                    strokeWidth={highlighted ? 2 : selected ? 1.5 : 0}
                                />
                                {box.w > MIN_LABEL_WIDTH && (
                                    <text x={5} y={ROW_HEIGHT / 2} dominantBaseline="central" fontSize={11} fill={textOnHeat(t)}>
                                        {label(d.data)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>

            <ProfilingTooltip open={!!hover} x={hover?.x ?? 0} y={hover?.y ?? 0}>
                {hover && <>
                    <TipTitle>{label(hover.node.data)}</TipTitle>
                    <TipRow label="Node type" value={hover.node.data.nodeType} />
                    <TipRow label="Self time" value={formatMs(hover.node.data.selfTime)} />
                    <TipRow label="Total time" value={formatMs(hover.node.data.totalTime)} />
                    <TipRow label="Start (rel.)" value={`+${formatMs(hover.node.data.startTime - batch.startTime)}`} />
                    {hover.node.data.loopIndex != null && <TipRow label="Loop index" value={hover.node.data.loopIndex} />}
                    {hover.node.data.phase && <TipRow label="React phase" value={hover.node.data.phase} />}
                    <TipDivider />
                    <TipRow label="Template" value={batch.fileName ?? "(inline)"} />
                    <TipRow label="Batch" value={`${TRIGGER_LABELS[batch.trigger]} · #${batch.renderId}`} />
                    <TipRow label="Cycle" value={batch.cycleId} />
                    {hover.node.data.source && (
                        <div style={{ marginTop: 6 }}>
                            <CodeBlock code={hover.node.data.source.slice(0, 400)} maxHeight={120} fontSize="0.66rem" />
                        </div>
                    )}
                </>}
            </ProfilingTooltip>
        </div>
    );
}

export default React.memo(FlameGraph);
